/**
 * What went out, under which heading, to whom.
 *
 * Amount and tax stay apart the whole way down the page. Added together they
 * make one bigger number that answers neither question a VAT-registered desk
 * asks: the net is what the business spent, the tax is what it reclaims.
 */
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { fmtMoney, fmtNum } from "../../ui";
import { Breakdown, MonthChart, Stat, StatStrip } from "./bits";
import type { ExpensesReport } from "../../../lib/reports";

export function ExpensesReportTab({ data }: { data: ExpensesReport }) {
  const t = useT();
  const { locale } = useI18n();
  const { totals } = data;
  // The fourth figure is a subtraction rather than a fifth number from the
  // server, so it can never disagree with the two it is made of.
  const unbilledCents = Math.max(0, totals.billableCents - totals.billedCents);

  return (
    <>
      <StatStrip columns={5}>
        <Stat label={t("reports.expenses.net")} value={fmtMoney(totals.amountCents)} sub={fmtNum(totals.count) + " " + t("reports.expenses.rowsUnit")} />
        <Stat label={t("reports.expenses.tax")} value={fmtMoney(totals.taxCents)} />
        <Stat label={t("reports.expenses.gross")} value={fmtMoney(totals.amountCents + totals.taxCents)} />
        <Stat label={t("reports.expenses.billable")} value={fmtMoney(totals.billableCents)} />
        <Stat label={t("reports.expenses.unbilled")} value={fmtMoney(unbilledCents)} tone={unbilledCents ? "bad" : undefined} />
      </StatStrip>

      <div className="crm-rep-grid">
        <MonthChart
          title={t("reports.expenses.chartTitle")}
          sub={t("reports.expenses.chartSub")}
          months={data.meta.months}
          locale={locale}
          lines={[
            { label: t("reports.expenses.net"), values: data.months.map((m) => m.amountCents / 100), color: "var(--color-warning)" },
            { label: t("reports.expenses.tax"), values: data.months.map((m) => m.taxCents / 100), color: "var(--color-muted-foreground)" },
          ]}
        />
      </div>

      <div className="crm-rep-grid is-split">
        <Breakdown
          title={t("reports.expenses.byCategory")}
          rows={data.byCategory.map((r) => ({
            key: r.category,
            label: r.category,
            weight: r.amountCents,
            value: fmtMoney(r.amountCents),
            sub: fmtNum(r.count),
          }))}
          empty={t("reports.empty")}
          color="var(--color-warning)"
        />
        <Breakdown
          title={t("reports.expenses.byVendor")}
          sub={t("reports.expenses.byVendorSub")}
          rows={data.byVendor.map((r) => ({
            key: r.vendor,
            label: r.vendor,
            weight: r.amountCents,
            value: fmtMoney(r.amountCents),
            sub: fmtNum(r.count),
          }))}
          empty={t("reports.empty")}
        />
      </div>

      <p className="crm-rep-note">{t("reports.expenses.note")}</p>
    </>
  );
}
